export interface Question {
  id: string;
  question: string;
  category: string;
  subcategory: string;
  scale: {
    min: number;
    max: number;
    labels: Record<number, string>;
  };
}

export interface Category {
  id: string;
  name: string;
  weight: number;
  description: string;
  color: string;
}

export const categories: Category[] = [
  {
    id: "data-infrastructure",
    name: "Data & Infrastructure",
    weight: 30,
    description: "Data quality, governance, security, and technical readiness",
    color: "accent",
  },
  {
    id: "culture-change",
    name: "Culture & Change",
    weight: 30,
    description: "Leadership buy-in, employee attitudes, and change management",
    color: "info",
  },
  {
    id: "strategic-alignment",
    name: "Strategic Alignment",
    weight: 20,
    description: "AI vision, budget commitment, and success metrics",
    color: "success",
  },
  {
    id: "skills-capability",
    name: "Skills & Capability",
    weight: 20,
    description: "AI literacy, technical skills, and training programs",
    color: "warning",
  },
];

export const questions: Question[] = [
  // Data & Infrastructure (30% weight) - 4 questions
  {
    id: "data-quality",
    question: "How would you rate the quality and consistency of data across your organisation?",
    category: "data-infrastructure",
    subcategory: "Data Quality & Accessibility",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Poor - Inconsistent, unreliable data",
        2: "Below Average - Significant quality issues",
        3: "Moderate - Some quality concerns",
        4: "Good - Generally reliable data",
        5: "Excellent - High quality, consistent data",
      },
    },
  },
  {
    id: "data-accessibility",
    question: "How easily can teams access the data they need for decision-making?",
    category: "data-infrastructure",
    subcategory: "Data Quality & Accessibility",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Very Difficult - Siloed, restricted access",
        2: "Difficult - Significant barriers",
        3: "Moderate - Some access challenges",
        4: "Easy - Most data readily available",
        5: "Very Easy - Self-service data access",
      },
    },
  },
  {
    id: "data-governance",
    question: "How mature are your data privacy, security, and compliance policies?",
    category: "data-infrastructure",
    subcategory: "Data Governance & Security",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Non-existent - No formal policies",
        2: "Basic - Informal guidelines",
        3: "Developing - Policies in progress",
        4: "Established - Comprehensive policies",
        5: "Fully Mature - Industry-leading governance",
      },
    },
  },
  {
    id: "technical-infrastructure",
    question: "How ready is your IT infrastructure (cloud, APIs, integrations) to support AI tools?",
    category: "data-infrastructure",
    subcategory: "Technical Infrastructure",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Not Ready - Legacy systems only",
        2: "Limited - Basic cloud adoption",
        3: "Moderate - Partial modernization",
        4: "Ready - Cloud-first, API-enabled",
        5: "Fully Ready - Modern, AI-optimized stack",
      },
    },
  },

  // Culture & Change (30% weight) - 4 questions
  {
    id: "leadership-buyin",
    question: "How actively does executive leadership champion AI initiatives?",
    category: "culture-change",
    subcategory: "Leadership Buy-in",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Not at All - No executive support",
        2: "Minimal - Passive awareness",
        3: "Moderate - Occasional support",
        4: "Active - Regular championship",
        5: "Strongly Champion - AI as strategic priority",
      },
    },
  },
  {
    id: "employee-attitudes",
    question: "How would you describe staff attitudes toward AI adoption?",
    category: "culture-change",
    subcategory: "Employee Attitudes",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Resistant - Fear and opposition",
        2: "Skeptical - Cautious concerns",
        3: "Neutral - Open but uncertain",
        4: "Positive - Eager to learn",
        5: "Enthusiastic - Active advocates",
      },
    },
  },
  {
    id: "change-management",
    question: "How effective are your processes for managing organisational change?",
    category: "culture-change",
    subcategory: "Change Management",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "No Process - Ad-hoc changes only",
        2: "Basic - Informal approach",
        3: "Developing - Some structured methods",
        4: "Effective - Proven methodologies",
        5: "Highly Effective - Mature change culture",
      },
    },
  },
  {
    id: "innovation-culture",
    question: "How comfortable is your organisation with experimentation and learning from failure?",
    category: "culture-change",
    subcategory: "Innovation Culture",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Risk Averse - Failure not tolerated",
        2: "Cautious - Limited experimentation",
        3: "Moderate - Some innovation accepted",
        4: "Open - Experimentation encouraged",
        5: "Embraces Experimentation - Fail-fast culture",
      },
    },
  },

  // Strategic Alignment (20% weight) - 3 questions
  {
    id: "ai-vision",
    question: "Does your organisation have a clear AI strategy aligned with business objectives?",
    category: "strategic-alignment",
    subcategory: "AI Vision",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "No Strategy - No AI roadmap",
        2: "Informal - Vague direction",
        3: "Emerging - Strategy in development",
        4: "Defined - Clear strategy exists",
        5: "Fully Aligned - AI embedded in business strategy",
      },
    },
  },
  {
    id: "budget-commitment",
    question: "How committed is leadership to investing in AI initiatives?",
    category: "strategic-alignment",
    subcategory: "Budget Commitment",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "No Budget - No allocated funds",
        2: "Limited - Minimal investment",
        3: "Moderate - Project-based funding",
        4: "Committed - Consistent investment",
        5: "Dedicated Budget - Strategic AI investment",
      },
    },
  },
  {
    id: "success-metrics",
    question: "Are there defined KPIs and success measures for AI projects?",
    category: "strategic-alignment",
    subcategory: "Success Metrics",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "None Defined - No measurement",
        2: "Basic - Informal tracking",
        3: "Developing - Some KPIs defined",
        4: "Established - Clear metrics in place",
        5: "Comprehensive - Full measurement framework",
      },
    },
  },

  // Skills & Capability (20% weight) - 3 questions
  {
    id: "ai-literacy",
    question: "What is the general level of AI understanding across your workforce?",
    category: "skills-capability",
    subcategory: "AI Literacy",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Very Low - No AI awareness",
        2: "Low - Basic awareness only",
        3: "Moderate - General understanding",
        4: "High - Good working knowledge",
        5: "Very High - Deep AI expertise",
      },
    },
  },
  {
    id: "technical-skills",
    question: "Do you have the technical talent needed to implement and maintain AI solutions?",
    category: "skills-capability",
    subcategory: "Technical Skills",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "Major Gaps - No AI talent",
        2: "Significant Gaps - Limited skills",
        3: "Some Gaps - Building capability",
        4: "Adequate - Most skills covered",
        5: "Fully Capable - Strong AI team",
      },
    },
  },
  {
    id: "training-programs",
    question: "How robust are your AI training and upskilling programs?",
    category: "skills-capability",
    subcategory: "Training Programs",
    scale: {
      min: 1,
      max: 5,
      labels: {
        1: "None - No training available",
        2: "Basic - Ad-hoc training",
        3: "Developing - Some programs exist",
        4: "Established - Structured programs",
        5: "Comprehensive - Full learning pathways",
      },
    },
  },
];

export const getQuestionsByCategory = (categoryId: string): Question[] => {
  return questions.filter((q) => q.category === categoryId);
};

export const getCategoryById = (categoryId: string): Category | undefined => {
  return categories.find((c) => c.id === categoryId);
};
